/* ============================================================
   The persistent left rail, frosted over the ground: the lockup with the
   day's ring around the sun, navigation with each page's own coloured icon,
   the project and tag scopes (a project row carries its progress ring, the
   way the mobile title does), the sync badge, and the account.
   ============================================================ */
import React from 'react';
import { NavLink } from 'react-router';
import { todayStr, useAuth, useStore, useSync, type HueName, type SyncPhase } from '@habishine/client-core';

import { Icon, type IconName } from '../components/Icon';
import { Ring } from '../components/Ring';
import { SunMark } from '../components/SunMark';
import { Avatar } from '../components/ui';
import { hueVar } from '../theme/hues';
import { useEditors } from './Editors';

export const PHASE: Record<SyncPhase, { label: string; icon: IconName; cls: string }> = {
  synced: { label: 'Synced', icon: 'check-circle', cls: 'text-success' },
  syncing: { label: 'Syncing…', icon: 'refresh-cw', cls: 'animate-spin text-frog' },
  offline: { label: 'Offline — saved here', icon: 'cloud-off', cls: 'text-replan' },
  error: { label: 'Sync failed — retrying', icon: 'cloud-off', cls: 'text-replan' },
};

/* each page has a colour, so the active item is a lit tile rather than a
   highlighted line — and the eye learns where it is by hue */
export const PAGES: { to: string; icon: IconName; label: string; hue: HueName; key: string }[] = [
  { to: '/today', icon: 'sun', label: 'Today', hue: 'yellow', key: 'T' },
  { to: '/plan', icon: 'calendar', label: 'Plan', hue: 'blue', key: 'P' },
  { to: '/review', icon: 'bar-chart', label: 'Review', hue: 'green', key: 'R' },
  { to: '/habits', icon: 'sparkle', label: 'Habits', hue: 'teal', key: 'H' },
];

function Item({ to, children, end, className = '' }: { to: string; children: React.ReactNode | ((active: boolean) => React.ReactNode); end?: boolean; className?: string }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `group/nav flex items-center gap-2.5 rounded-[12px] px-2.5 py-[7px] text-[13.5px] transition-[background-color,color,box-shadow] duration-150 ${isActive ? 'bg-card font-semibold text-ink shadow-[0_1px_2px_rgba(20,20,25,0.06),0_6px_16px_-8px_rgba(20,20,25,0.18)]' : 'text-ink-2 hover:bg-card/60 hover:text-ink'} ${className}`
      }
    >
      {({ isActive }) => (typeof children === 'function' ? children(isActive) : children)}
    </NavLink>
  );
}

export function Sidebar() {
  const { activeProjects, activeTags, projectOpenCount, projectProgress, tagOpenCount, habitsForDay, tasksForDay, doneForDay } = useStore();
  // the day, as a ring around the sun: due habits and the day's tasks, done over total
  const today = todayStr();
  const dueToday = habitsForDay(today).filter((h) => h.dueToday);
  const dayTotal = dueToday.length + tasksForDay(today).length + doneForDay(today).length;
  const dayDone = dueToday.filter((h) => h.done).length + doneForDay(today).length;
  const sync = useSync();
  const { user } = useAuth();
  const { editProject } = useEditors();
  const projects = activeProjects();
  const tags = activeTags();
  const phase = PHASE[sync.phase];

  return (
    <aside className="glass relative z-[2] hidden h-full w-[256px] shrink-0 flex-col border-y-0 border-l-0 border-r p-3.5 md:flex">
      {/* the lockup: the mark inside the day's ring, the wordmark as a logotype */}
      <NavLink to="/today" className="mb-4 flex items-center gap-3 px-1.5 pt-1.5" title={dayTotal ? `${dayDone} of ${dayTotal} done today` : 'Today'}>
        <span className="relative flex h-10 w-10 items-center justify-center">
          <span className="absolute inset-0"><Ring size={40} stroke={3} hue="yellow" pct={dayTotal ? dayDone / dayTotal : 0} track="sunk" /></span>
          <SunMark size={24} />
        </span>
        <span className="wordmark text-[22px] text-ink">habishine</span>
      </NavLink>

      <button
        type="button"
        onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true }))}
        className="mb-4 flex w-full items-center gap-2 rounded-[12px] border border-hairline bg-card px-3 py-2 text-left text-[13px] text-placeholder shadow-[0_1px_2px_rgba(20,20,25,0.04)] transition hover:text-meta hover:shadow-card"
      >
        <Icon name="search" size={14} strokeWidth={2.4} /> Search or jump… <kbd className="kbd ml-auto">⌘K</kbd>
      </button>

      <nav className="space-y-0.5">
        {PAGES.map((p) => (
          <Item key={p.to} to={p.to}>
            {(active) => (
              <>
                <span
                  className="flex h-6 w-6 items-center justify-center rounded-[8px] transition-colors duration-150"
                  style={active ? { background: hueVar(p.hue, 't'), color: hueVar(p.hue, 'k') } : { color: 'var(--tertiary)' }}
                >
                  <Icon name={p.icon} size={15} strokeWidth={2.4} />
                </span>
                <span className="flex-1">{p.label}</span>
                <kbd className="kbd opacity-0 transition-opacity group-hover/nav:opacity-100">{p.key}</kbd>
              </>
            )}
          </Item>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <Section title="Projects" action={<button type="button" aria-label="New project" onClick={() => editProject(null)} className="rounded-full p-1 text-tertiary transition hover:bg-card hover:text-frog"><Icon name="plus" size={14} strokeWidth={2.4} /></button>}>
          {projects.length === 0 ? <Hint>Press + to make one, or type #Name in capture.</Hint> : null}
          {projects.map((p) => {
            const prog = projectProgress(p.id);
            return (
              <Item key={p.id} to={`/project/${p.id}`}>
                <span className="flex w-6 items-center justify-center">
                  {prog.total > 0 ? <Ring size={16} stroke={2.8} hue={p.hue} pct={prog.done / prog.total} check={prog.done === prog.total} /> : <span className="h-2.5 w-2.5 rounded-full" style={{ background: hueVar(p.hue, 'c') }} />}
                </span>
                <span className="flex-1 truncate">{p.name}</span>
                <Count n={projectOpenCount(p.id)} />
              </Item>
            );
          })}
        </Section>

        <Section title="Tags">
          {tags.length === 0 ? <Hint>Type @name in capture to make one.</Hint> : null}
          {tags.map((t) => (
            <Item key={t.id} to={`/tag/${t.id}`}>
              <span className="flex w-6 items-center justify-center" style={{ color: hueVar(t.hue, 'c') }}><Icon name="tag" size={15} strokeWidth={2.3} /></span>
              <span className="flex-1 truncate">{t.name}</span>
              <Count n={tagOpenCount(t.id)} />
            </Item>
          ))}
        </Section>
      </div>

      <div className="mt-2 space-y-1 border-t border-hairline pt-3">
        <button type="button" onClick={sync.syncNow} title="Sync now" className="flex w-full items-center gap-2 rounded-[10px] px-2.5 py-1.5 text-[12px] text-meta transition hover:bg-card/60">
          <span className={phase.cls}><Icon name={phase.icon} size={13} strokeWidth={2.4} /></span>
          <span className="truncate">{phase.label}</span>
          {sync.pending > 0 ? <span className="ml-auto rounded-full bg-card px-1.5 text-[11px] font-bold text-ink-2">{sync.pending}</span> : null}
        </button>
        <Item to="/settings">
          <Avatar email={user?.email} size={24} />
          <span className="min-w-0 flex-1 truncate text-[13px]">{user?.email ?? 'Settings'}</span>
          <span className="text-tertiary"><Icon name="settings" size={14} strokeWidth={2.2} /></span>
        </Item>
      </div>
    </aside>
  );
}

function Section({ title, action, children }: { title: string; action?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-6">
      <div className="mb-1 flex items-center justify-between pl-3 pr-1.5 text-[10.5px] font-bold uppercase tracking-[1px] text-tertiary">{title}{action}</div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}
const Hint = ({ children }: { children: string }) => <p className="px-3 py-1 text-xs leading-relaxed text-tertiary">{children}</p>;
const Count = ({ n }: { n: number }) => (n > 0 ? <span className="num text-[11.5px] text-tertiary">{n}</span> : null);
